import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient, useAuth } from "../../App";
import { toast } from "sonner";
import { ChevronLeft, Plus, Trash2, Save } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";

const ADMIN_ROLES = new Set(["COO", "Admin"]);

export default function AIContentAdmin() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(null); // new service draft

  const isAdmin = ADMIN_ROLES.has(user?.role);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        apiClient.get("/ai-content/services?include_inactive=true"),
        apiClient.get("/ai-content/categories"),
      ]);
      setServices(s.data || []);
      setCategories(c.data || []);
    } catch {
      toast.error("Failed to load");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#000000] text-[#F4F2EE] flex items-center justify-center">
        <div className="text-center">
          <div className="text-[15px] font-semibold">Admin only</div>
          <div className="text-[13px] text-[#8A8783] mt-1">Pricing Admin requires COO/Admin role.</div>
        </div>
      </div>
    );
  }

  const persist = async (svc) => {
    try {
      await apiClient.patch(`/ai-content/services/${svc.id}`, svc);
      toast.success("Saved");
    } catch {
      toast.error("Save failed");
    }
  };

  const updateLocal = (id, patch) => {
    setServices(prev => prev.map(s => s.id === id ? { ...s, ...patch } : s));
  };

  const removeService = async (id) => {
    if (!window.confirm("Delete this service permanently?")) return;
    try {
      await apiClient.delete(`/ai-content/services/${id}`);
      toast.success("Deleted");
      fetchAll();
    } catch {
      toast.error("Delete failed");
    }
  };

  const createService = async () => {
    if (!draft?.name?.trim() || !draft?.category) { toast.error("Name and category required"); return; }
    try {
      await apiClient.post("/ai-content/services", draft);
      toast.success("Created");
      setDraft(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Create failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#000000] text-[#F4F2EE]" data-testid="ai-content-admin">
      <div className="max-w-[1200px] mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="text-[#8A8783] hover:text-white">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div>
              <div className="text-[11px] uppercase tracking-[0.14em] text-[#8A8783]">TRP AI Content Studio</div>
              <h1 className="text-[24px] font-light tracking-tight">Pricing Admin</h1>
            </div>
          </div>
          <Button onClick={() => setDraft({ name: "", category: categories[0]?.key || "reel_production", vendor_cost: 0, default_markup_percent: 50, default_pricing_mode: "percent", setup_fee: 0, billing: "monthly", is_active: true })} className="bg-white text-[#000000] hover:bg-white/90" data-testid="add-service-btn">
            <Plus className="w-4 h-4 mr-1.5" /> New Service
          </Button>
        </div>

        {/* Draft new service */}
        {draft && (
          <div className="rounded-2xl border border-white/[0.16] bg-[#000000] p-5 mb-6" data-testid="service-draft">
            <div className="flex items-center justify-between mb-4">
              <div className="text-[15px] font-semibold text-[#F4F2EE]">New Service</div>
              <Button variant="ghost" size="sm" onClick={() => setDraft(null)} className="text-[#C9C6C1]">Cancel</Button>
            </div>
            <ServiceForm service={draft} onChange={(p) => setDraft({ ...draft, ...p })} categories={categories} />
            <div className="flex justify-end mt-4">
              <Button onClick={createService} className="bg-white text-[#000000] hover:bg-white/90"><Save className="w-4 h-4 mr-1.5" /> Save Service</Button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-[#565350] text-sm">Loading…</div>
        ) : (
          <div className="space-y-6">
            {categories.map(cat => {
              const items = services.filter(s => s.category === cat.key);
              if (items.length === 0) return null;
              return (
                <div key={cat.key}>
                  <div className="text-[11px] uppercase tracking-[0.14em] text-[#8A8783] mb-2">{cat.label}</div>
                  <div className="rounded-2xl border border-white/[0.08] bg-[#000000] divide-y divide-white/[0.06]">
                    {items.map(svc => (
                      <div key={svc.id} className="p-4" data-testid={`admin-service-${svc.id}`}>
                        <ServiceForm service={svc} onChange={(p) => updateLocal(svc.id, p)} categories={categories} />
                        <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/[0.06]">
                          <div className="flex items-center gap-3">
                            <Switch checked={!!svc.is_active} onCheckedChange={(v) => { updateLocal(svc.id, { is_active: v }); persist({ ...svc, is_active: v }); }} data-testid={`service-active-${svc.id}`} />
                            <span className="text-[12px] text-[#8A8783]">{svc.is_active ? "Active" : "Inactive"}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button variant="ghost" size="sm" onClick={() => removeService(svc.id)} className="text-[#8A8783] hover:text-red-400"><Trash2 className="w-3.5 h-3.5 mr-1" /> Delete</Button>
                            <Button size="sm" onClick={() => persist(svc)} className="bg-white text-[#000000] hover:bg-white/90" data-testid={`save-service-${svc.id}`}><Save className="w-3.5 h-3.5 mr-1" /> Save</Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function ServiceForm({ service, onChange, categories }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <div className="col-span-2">
        <Label className="text-[11px] uppercase tracking-wider text-[#8A8783] mb-1 block">Name</Label>
        <Input value={service.name || ""} onChange={(e) => onChange({ name: e.target.value })} className="bg-[#000000] border-white/[0.08] text-white" />
      </div>
      <div>
        <Label className="text-[11px] uppercase tracking-wider text-[#8A8783] mb-1 block">Category</Label>
        <Select value={service.category} onValueChange={(v) => onChange({ category: v })}>
          <SelectTrigger className="bg-[#000000] border-white/[0.08] text-white"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#000000] border-white/[0.08] text-white">
            {categories.map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-[11px] uppercase tracking-wider text-[#8A8783] mb-1 block">Billing</Label>
        <Select value={service.billing || "monthly"} onValueChange={(v) => onChange({ billing: v })}>
          <SelectTrigger className="bg-[#000000] border-white/[0.08] text-white"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#000000] border-white/[0.08] text-white">
            <SelectItem value="per_reel">Per Reel</SelectItem>
            <SelectItem value="monthly">Monthly</SelectItem>
            <SelectItem value="one_time">One-time</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-[11px] uppercase tracking-wider text-[#8A8783] mb-1 block">Vendor Cost</Label>
        <Input type="number" value={service.vendor_cost ?? 0} onChange={(e) => onChange({ vendor_cost: Number(e.target.value) || 0 })} className="bg-[#000000] border-white/[0.08] text-white tabular-nums" />
      </div>
      <div>
        <Label className="text-[11px] uppercase tracking-wider text-[#8A8783] mb-1 block">Default Mode</Label>
        <Select value={service.default_pricing_mode || "percent"} onValueChange={(v) => onChange({ default_pricing_mode: v })}>
          <SelectTrigger className="bg-[#000000] border-white/[0.08] text-white"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#000000] border-white/[0.08] text-white">
            <SelectItem value="percent">% Markup</SelectItem>
            <SelectItem value="fixed">Fixed Markup</SelectItem>
            <SelectItem value="manual">Manual Price</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-[11px] uppercase tracking-wider text-[#8A8783] mb-1 block">Default Markup %</Label>
        <Input type="number" value={service.default_markup_percent ?? 50} onChange={(e) => onChange({ default_markup_percent: Number(e.target.value) || 0 })} className="bg-[#000000] border-white/[0.08] text-white tabular-nums" />
      </div>
      <div>
        <Label className="text-[11px] uppercase tracking-wider text-[#8A8783] mb-1 block">Setup Fee</Label>
        <Input type="number" value={service.setup_fee ?? 0} onChange={(e) => onChange({ setup_fee: Number(e.target.value) || 0 })} className="bg-[#000000] border-white/[0.08] text-white tabular-nums" />
      </div>
      <div>
        <Label className="text-[11px] uppercase tracking-wider text-[#8A8783] mb-1 block">Reels / month</Label>
        <Input type="number" value={service.monthly_reels ?? ""} onChange={(e) => onChange({ monthly_reels: e.target.value === "" ? null : Number(e.target.value) })} placeholder="optional" className="bg-[#000000] border-white/[0.08] text-white tabular-nums" />
      </div>
    </div>
  );
}
